import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { propertyImageUrl } from '../lib/propertyImage';
import { Property, QuoteResponse } from '../types';
import './SearchPage.css';

// ------------------------------------------------------------
// 日付ユーティリティ（ローカルタイムで YYYY-MM-DD を扱う）
// ------------------------------------------------------------
function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function parseISO(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
// YYYY-MM-DD 形式かつ実在する日付かを判定（URLクエリ検証用）
function isValidISODate(s: string | null): s is string {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = parseISO(s);
  return toISO(d) === s;
}

// ------------------------------------------------------------
// URLクエリからの初期値（公式サイト連携 H1/H2）
//   ?checkin=YYYY-MM-DD&checkout=YYYY-MM-DD&guests=N&property=prop-00X
//   - 過去日・不正値・checkout<=checkin の場合は日付を両方無視
//   - guests は 1〜99 の整数のみ。範囲外は既定値（2）
//   - property は物件一覧取得後に存在チェック（不明IDは無視して横断）
// ------------------------------------------------------------
function readInitialQuery(sp: URLSearchParams, todayISO: string) {
  const ci = sp.get('checkin');
  const co = sp.get('checkout');
  let checkin = '';
  let checkout = '';
  if (isValidISODate(ci) && isValidISODate(co) && ci >= todayISO && co > ci) {
    checkin = ci;
    checkout = co;
  }
  const g = Number(sp.get('guests'));
  const guests = Number.isInteger(g) && g >= 1 && g <= 99 ? g : 2;
  const property = (sp.get('property') || '').trim();
  return { checkin, checkout, guests, property };
}

function nightsBetween(checkin: string, checkout: string): number {
  return Math.round(
    (parseISO(checkout).getTime() - parseISO(checkin).getTime()) / 86400000
  );
}

// 各物件の検索結果（1物件 = 1 quote 呼び出しの結果）
interface SearchResult {
  property: Property;
  status: 'available' | 'unavailable' | 'error';
  quote?: QuoteResponse;
  message?: string; // 満室・エラー時の理由
}

function SearchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ---------- 検索条件 ----------
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const todayISO = toISO(today);

  const [searchParams] = useSearchParams();
  // 初回レンダー時のみクエリを読む（以降の入力変更でURLは書き換えない）
  const initial = useMemo(
    () => readInitialQuery(searchParams, todayISO),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [checkin, setCheckin] = useState<string>(initial.checkin);
  const [checkout, setCheckout] = useState<string>(initial.checkout);
  const [guestCount, setGuestCount] = useState<number>(initial.guests);

  // 施設指定（H2）：有効な物件IDのときだけセット。null = 横断検索
  const [focusPropertyId, setFocusPropertyId] = useState<string | null>(null);
  const [showAllResults, setShowAllResults] = useState(false);
  const autoSearchDone = useRef(false);

  // ---------- 物件マスタ ----------
  const [properties, setProperties] = useState<Property[]>([]);
  const [propLoading, setPropLoading] = useState(true);
  const [propError, setPropError] = useState<string | null>(null);

  // ---------- 検索結果 ----------
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // ---------- 物件一覧を取得 ----------
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/properties');
        if (!res.ok) throw new Error('Failed to fetch properties');
        const data: Property[] = await res.json();
        setProperties(data);
        if (initial.property && data.some((p) => p.id === initial.property)) {
          setFocusPropertyId(initial.property);
        }
      } catch (err) {
        setPropError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setPropLoading(false);
      }
    })();
  }, []);

  const fmtMoney = (n: number, currency = 'JPY') =>
    new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'JPY' ? 0 : 2,
    }).format(n);

  // ---------- 入力バリデーション ----------
  const nights =
    checkin && checkout ? nightsBetween(checkin, checkout) : 0;
  const datesValid =
    !!checkin && !!checkout && nights >= 1 && checkin >= todayISO;

  // ---------- 空室を検索（3物件へ並列 quote） ----------
  const handleSearch = async () => {
    setFormError(null);
    if (!checkin || !checkout) {
      setFormError(t('search.errorSelectDates'));
      return;
    }
    if (nights < 1) {
      setFormError(t('search.errorCheckoutAfterCheckin'));
      return;
    }
    if (checkin < todayISO) {
      setFormError(t('search.errorPastDate'));
      return;
    }

    setSearching(true);
    setResults(null);

    // 各物件に対して見積もりを並列取得。
    // quote が成功 → 空室あり＋料金。409/その他 → 満室 or 条件不一致。
    const settled = await Promise.all(
      properties.map(async (property): Promise<SearchResult> => {
        try {
          const res = await fetch('/api/reservations/quote', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              property_id: property.id,
              checkin_date: checkin,
              checkout_date: checkout,
              guest_count: guestCount,
            }),
          });
          const data = await res.json();
          if (res.ok) {
            return {
              property,
              status: 'available',
              quote: data as QuoteResponse,
            };
          }
          // 空室なし・最低泊数未満・人数超過などはすべて「予約不可」として扱う
          return {
            property,
            status: 'unavailable',
            message: data?.error || t('property.unavailable'),
          };
        } catch (err) {
          return {
            property,
            status: 'error',
            message:
              err instanceof Error ? err.message : t('common.error'),
          };
        }
      })
    );

    setResults(settled);
    setSearching(false);
  };

  // ---------- クエリ指定時の自動検索（H1） ----------
  // 物件一覧の取得完了後、日付が揃っていれば1回だけ自動実行する
  useEffect(() => {
    if (autoSearchDone.current || propLoading || propError) return;
    if (properties.length === 0) return;
    autoSearchDone.current = true;
    if (initial.checkin && initial.checkout) {
      void handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propLoading, propError, properties]);

  // ---------- 「予約する」→ 既存のゲスト情報ページへ ----------
  const handleReserve = (r: SearchResult) => {
    if (r.status !== 'available' || !r.quote) return;
    navigate('/booking/guest', {
      state: {
        property: r.property,
        checkin,
        checkout,
        guestCount,
        quote: r.quote,
      },
    });
  };

  if (propLoading) return <div className="loading">{t('common.loading')}</div>;
  if (propError)
    return (
      <div className="error">
        {t('common.error')}: {propError}
      </div>
    );

  const maxGuestsAll = properties.reduce(
    (m, p) => Math.max(m, p.max_guests),
    10
  );

  // 施設指定時は当該物件のみ表示（「他の施設も見る」で全件表示）
  const focusProperty = focusPropertyId
    ? properties.find((p) => p.id === focusPropertyId) || null
    : null;
  const visibleResults =
    results && focusProperty && !showAllResults
      ? results.filter((r) => r.property.id === focusProperty.id)
      : results;
  const hiddenCount = results && visibleResults ? results.length - visibleResults.length : 0;

  return (
    <div className="search-page">
      <div className="container search-container">
        <h1 className="search-title">{t('search.title')}</h1>
        <p className="search-subtitle">{t('search.subtitle')}</p>

        {/* ============ 検索フォーム ============ */}
        <section className="search-form">
          <div className="search-field">
            <label htmlFor="s-checkin">{t('search.checkin')}</label>
            <input
              id="s-checkin"
              type="date"
              min={todayISO}
              value={checkin}
              onChange={(e) => {
                setCheckin(e.target.value);
                // チェックイン >= チェックアウトになったらチェックアウトをクリア
                if (checkout && e.target.value >= checkout) {
                  setCheckout('');
                }
              }}
            />
          </div>

          <div className="search-field">
            <label htmlFor="s-checkout">{t('search.checkout')}</label>
            <input
              id="s-checkout"
              type="date"
              min={checkin || todayISO}
              value={checkout}
              onChange={(e) => setCheckout(e.target.value)}
            />
          </div>

          <div className="search-field">
            <label htmlFor="s-guests">{t('search.guests')}</label>
            <select
              id="s-guests"
              value={guestCount}
              onChange={(e) => setGuestCount(Number(e.target.value))}
            >
              {Array.from({ length: Math.max(maxGuestsAll, guestCount) }, (_, i) => i + 1).map(
                (n) => (
                  <option key={n} value={n}>
                    {n} {t('booking.guests')}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="search-field search-submit">
            <button
              className="btn btn-primary search-btn"
              onClick={handleSearch}
              disabled={!datesValid || searching}
            >
              {searching ? t('search.searching') : t('search.searchButton')}
            </button>
          </div>
        </section>

        {formError && <p className="search-error">{formError}</p>}

        {nights > 0 && (
          <p className="search-nights-note">
            {nights} {t('booking.nights')} / {guestCount} {t('booking.guests')}
          </p>
        )}

        {/* ============ 検索結果 ============ */}
        {searching && (
          <div className="search-loading">{t('search.searching')}</div>
        )}

        {focusProperty && (
          <p className="search-focus-note">
            {t('search.focusProperty', { name: focusProperty.name })}{' '}
            <button
              type="button"
              className="btn-link"
              onClick={() => {
                setFocusPropertyId(null);
                setShowAllResults(true);
              }}
            >
              {t('search.clearFocus')}
            </button>
          </p>
        )}

        {results && visibleResults && !searching && (
          <section className="results">
            <h2 className="results-heading">{t('search.availableHomes')}</h2>
            <div className="results-grid">
              {visibleResults.map((r) => (
                <div
                  key={r.property.id}
                  className={`result-card ${
                    r.status === 'available' ? 'is-available' : 'is-unavailable'
                  }`}
                >
                  <div className="result-image">
                    <img
                      src={propertyImageUrl(r.property.id)}
                      alt={r.property.name}
                    />
                  </div>

                  <div className="result-body">
                    <h3 className="result-name">{r.property.name}</h3>

                    {r.status === 'available' && r.quote ? (
                      <>
                        <div className="result-status available">
                          <span className="dot" /> {t('property.available')}
                        </div>
                        <div className="result-price">
                          {fmtMoney(r.quote.total_price, r.quote.currency)}
                          <span className="result-price-note">
                            {' '}
                            / {r.quote.nights} {t('booking.nights')}（
                            {t('booking.total')}）
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="result-status unavailable">
                          <span className="dot" /> {t('property.unavailable')}
                        </div>
                        {r.message && (
                          <div className="result-msg">{r.message}</div>
                        )}
                      </>
                    )}

                    <div className="result-actions">
                      <Link
                        to={`/property/${r.property.id}`}
                        className="btn btn-outline"
                      >
                        {t('search.viewDetails')}
                      </Link>
                      <button
                        className="btn btn-primary"
                        disabled={r.status !== 'available'}
                        onClick={() => handleReserve(r)}
                      >
                        {t('search.reserve')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {visibleResults.every((r) => r.status !== 'available') && (
              <p className="results-empty">{t('search.noAvailable')}</p>
            )}

            {hiddenCount > 0 && (
              <div className="results-show-all">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowAllResults(true)}
                >
                  {t('search.showOtherProperties', { count: hiddenCount })}
                </button>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default SearchPage;
